import _ from 'underscore';
import * as putil from '@bingads-webui-universal/primitive-utilities';
import { Base } from './base';
import * as number from './string-number';

export { number };
/**
 * Get length of a string, if distinguish CJK in string, each CJK character counts as 2 charts
 * otherwise, count as 1 chart
 * @param {string} str - string to compute length
 * @param {boolean} distinguishCJK - whether distinguish CJK in string,
 * if not, the function equal to _.size, default value is false
 * @returns {number} - the length of string
 */
export function getLength(str: string, distinguishCJK: boolean = false): number {
  if (!distinguishCJK) {
    return _.size(str);
  }

  let counts = _.countBy(str, (c) => (putil.hasCJK(c) ? 'cjk' : 'ascii'));

  counts = _.defaults(counts, {
    cjk: 0,
    ascii: 0,
  });

  return (counts.cjk * 2) + counts.ascii;
}

/**
 * Define a validator check that the value is a string
 * @param {I18nModel} i18n - will be called in Base class
 */
class Type extends Base {
  isValidValue(value: string): boolean {
    return _.isString(value);
  }
}

/**
 * Define a validator to check the maximum size of a string
 * @param {number} length - the max length of string
 * @param {I18nModel} i18n - will be called with args 'Validation_String_Max_Length'
 * @param {boolean} distinguishCJK - whether to distinguish CJK in size check,
 * if true, one CJK character counts as 2
 */
class Size extends Base {
  distinguishCJK: boolean;

  length: number;

  constructor(length: number, i18n: I18nModel, distinguishCJK: boolean = true) {
    super(i18n);

    this.blockInput = false;
    this.length = length || 0;
    this.distinguishCJK = distinguishCJK;
    this.message = i18n.getString('Validation_String_Max_Length', { maxlength: this.length });
  }

  isValidValue(value: string): boolean {
    return _.isString(value) && putil.getLength(value, this.distinguishCJK) <= this.length;
  }
}

/**
 * Define a validator to check the minimum size of a string
 * @param {number} length - the min length of string
 * @param {I18nModel} i18n - will be called with args 'Validation_String_Min_Length'
 * @param {boolean} distinguishCJK - whether to distinguish CJK in size check,
 * if true, one CJK character counts as 2
 */
class MinSize extends Base {
  distinguishCJK: boolean;

  length: number;

  constructor(length: number, i18n: I18nModel, distinguishCJK: boolean = true) {
    super(i18n);

    this.blockInput = false;
    this.length = length || 0;
    this.distinguishCJK = distinguishCJK;
    this.message = i18n.getString('Validation_String_Min_Length', { minlength: this.length });
  }

  isValidValue(value: string): boolean {
    return _.isString(value) && putil.getLength(value, this.distinguishCJK) >= this.length;
  }
}

/**
 * Define a validator to check whether a value contains XSS code
 * @param {I18nModel} i18n - will be called with args 'Validation_String_Invalid_Characters'
 */
class XSS extends Base {
  constructor(i18n: I18nModel) {
    super(i18n);

    this.message = i18n.getString('Validation_String_Invalid_Characters');
  }

  isValidValue(value: string): boolean {
    return _.isString(value) && !(/<[^>]*>/).exec(value);
  }
}

/**
 * Define a validator to check whether a value matches a pattern
 * @param {string|RegExp} pattern - a pattern to test value
 * @param {I18nModel} i18n - will be called with args 'Validation_String_Invalid_Characters'
 */
class Pattern extends Base {
  pattern: RegExp;

  constructor(patt: string | RegExp, i18n: I18nModel) {
    super(i18n);

    this.pattern = _.isString(patt) ? new RegExp(patt) : patt;
    this.message = i18n.getString('Validation_String_Invalid_Characters');
  }

  isValidValue(value: string): boolean {
    return this.pattern.test(value);
  }
}

/**
 * Create a string type validator instance
 * @param {I18nModel} i18n - provide callback getString to get error message
 * @return {Base} - string type validator
 */
export function type(i18n: I18nModel): Base {
  return new Type(i18n);
}

/**
 * Create a string size validator instance (max length)
 * @param {number} length - the max length of string
 * @param {boolean} distinguishCJK - whether to distinguish CJK in size check
 * @param {I18nModel} i18n - provide getString to get error message
 * @returns {Base} - string size validator
 */
export function size(length: number, distinguishCJK: boolean, i18n: I18nModel): Base {
  return new Size(length, i18n, distinguishCJK);
}

/**
 * Create a string size validator instance (min length)
 * @param {number} length - the min length of string
 * @param {boolean} distinguishCJK - whether to distinguish CJK in size check
 * @param {I18nModel} i18n - provide getString to get error message
 * @returns {Base} - string size validator
 */
export function minSize(length: number, distinguishCJK: boolean, i18n: I18nModel): Base {
  return new MinSize(length, i18n, distinguishCJK);
}

/**
 * Create a validator to check whether a value contains XSS code
 * @param {I18nModel} i18n - will be called with args 'Validation_String_Invalid_Characters'
 * @returns {Base} - string xss validator
 */
export function xss(i18n: I18nModel): Base {
  return new XSS(i18n);
}

/**
 * Create a validator to check whether a value matches a pattern
 * @param {string|RegExp} patt - a pattern to test value
 * @param {I18nModel} i18n - will be called with args 'Validation_String_Invalid_Characters'
 * @returns {Base} - string pattern validator
 */
export function pattern(patt: string | RegExp, i18n: I18nModel): Base {
  return new Pattern(patt, i18n);
}
